import { P2GBox, P2GChip, P2GContainer, P2GIcon, P2GText, SvgTickCircle } from '@parcel2go/core-ui';
import { AppCard } from '~/components/card/app-card';
import { SidePanelContent } from '~/components/sidepanel-section';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import Loading from '~/components/loading/Loading';
import { OnboardingTitle } from '../rules/onboarding-title';
import { OnboardingActionBar } from '../intro/onboarding-action-bar';
import { AchievementType } from '~/apps/smartsend/lib/context/ProfileContext';
import { OnboardingScreenContentWrapper } from '../../OnboardingScreenContentWrapper';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
const QuotationBox = lazy(() => import('./QuotationBox'));

type props = {
  isXs: boolean;
  justLinked: string | null;
  actions: string[];
  done: AchievementType[];
  onDone: () => void;
};

const Row = ({ row, currentRow, children }: { row: number; currentRow: number; children: React.ReactNode }) => {
  const isDone = currentRow > row;
  return (
    <AppCard>
      <P2GBox p={2} sx={{ opacity: isDone ? 1 : 0.5 }}>
        <P2GText component="div" display="flex" flexWrap="nowrap" alignItems="center" gap={1}>
          <P2GIcon size="sm" palette={isDone ? 'success' : undefined}>
            {isDone ? <SvgTickCircle /> : <Loading />}
          </P2GIcon>
          {children}
        </P2GText>
      </P2GBox>
    </AppCard>
  );
};

const CompleteScreen = ({ justLinked, onDone, done }: props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [counter, setCounter] = useState(0);

  const steps = useMemo(() => {
    const output: { default: string; key: string; values?: { [key: string]: string } }[] = [];

    // Only show these steps if we actually linked an account
    if (done.includes('SmartSendActionAccountLinked')) {
      output.push({
        default: 'Connecting to your {{store}} store',
        key: 'SETUP_COMPLETE_STEP_CONNECTING',
        values: { store: justLinked || '' }
      });
      output.push({ default: 'Importing sold items from your store', key: 'SETUP_COMPLETE_STEP_IMPORTING' });
    }

    // Only show this step if we also set up some rules
    if (done.includes('SmartSendActionRuleAdded')) {
      output.push({
        default: 'Converting your defaults into shipping rules',
        key: 'SETUP_COMPLETE_STEP_CONVERTING_RULES'
      });

      output.push({
        default: 'Saving them in your Rule Manager — your home for shipping rules',
        key: 'SETUP_COMPLETE_STEP_SAVING_RULES'
      });

      // Only show this step if we also linked an account
      if (done.includes('SmartSendActionAccountLinked')) {
        output.push({
          default: 'Applying those rules to any missing information',
          key: 'SETUP_COMPLETE_STEP_APPLYING'
        });
      }
    }

    if (done.includes('SmartSendActionCsvUploaded')) {
      output.push({
        default: 'CSV data imported',
        key: 'SETUP_COMPLETE_STEP_CSV_IMPORT'
      });
    }

    // At least show something
    if (output.length === 0) {
      output.push({
        default: 'Preparing your account',
        key: 'SETUP_COMPLETE_STEP_PREPARING'
      });
    }

    return output;
  }, [done, justLinked]);

  useEffect(() => {
    if (counter > steps.length) {
      onDone();
    }

    const timer = setTimeout(() => setCounter(counter + 1), 1500);
    return () => clearTimeout(timer);
  }, [counter, onDone, steps.length]);

  return (
    <>
      <OnboardingScreenContentWrapper>
        <P2GContainer maxWidth="md">
          <SidePanelContent>
            <OnboardingTitle
              text="Your Smart Send journey starts <span>here!</span>"
              translationKey="SETUP_COMPLETE_TITLE"
            />
            <P2GText variant="subtitle1">
              {t('SETUP_COMPLETE_SUBTITLE', 'We are importing your orders and applying your defaults.')}
            </P2GText>
          </SidePanelContent>
        </P2GContainer>
        <P2GBox mt={4} mb={4}>
          <P2GChip label={t('SETUP_COMPLETE_CHIP_LABEL', 'Commencing launch!')} />
        </P2GBox>
        <Suspense>
          <QuotationBox
            contentKey="smartsend-setup-complete-quote"
            wrapper={(x) => (
              <P2GBox mt={4} mb={4}>
                {x}
              </P2GBox>
            )}
          />
        </Suspense>
        <P2GContainer maxWidth="md">
          <P2GBox display="flex" flexDirection="column" gap={1}>
            {steps.map((step, index) => (
              <Row key={step.key} row={index} currentRow={counter}>
                {t(step.key, { defaultValue: step.default, ...step.values })}
              </Row>
            ))}
          </P2GBox>
        </P2GContainer>
      </OnboardingScreenContentWrapper>
      <OnboardingActionBar>
        <P2GText variant="subtitle2">
          {t('SETUP_COMPLETE_ACTION_BAR_TEXT', 'Ready to send your first parcel — from now on, shipping runs itself.')}
        </P2GText>
      </OnboardingActionBar>
    </>
  );
};
export default CompleteScreen;
